import { Label } from '@aws-amplify/ui-react';
import { FC } from 'react';
import { BarLoader } from 'react-spinners';
import TrashIcon from '@/assets/trash.svg';
import { Button } from '@/components/ui/button';
import { InfoBar } from '@/components/ui/info-bar';
import { SectionHeadingGroup } from '@/components/ui/section-heading-group';
import { useCurrentOrganisation } from '@/hooks/use-current-organisation';
import { usePolicies } from '@/hooks/use-policies';
import { api } from '@/redux/api';
import { AddressBookErrorDetails } from './address-book-error-details';
import { CreateContactForm } from './create-contact-form';

export const CurrentAddressBooksView: FC = () => {
  const {
    data: policies,
    isLoading: policiesLoading,
    isUninitialized: policiesUninitialized,
    isError: policiesError,
  } = usePolicies();
  const { organisation } = useCurrentOrganisation();

  const addressBookId = policies?.addressBook.defaultAddressBook.addressBookId;
  const {
    data: addressBook,
    isFetching,
    isError,
    error,
  } = api.useGetAddressBookWithContactsQuery(
    {
      organisationId: organisation?.id ?? -1,
      actionType: 'getAddressBookWithContacts',
      addressBookId: addressBookId ?? '',
    },
    { skip: !organisation?.id || !addressBookId },
  );
  const [deleteAddressBookContact] = api.useDeleteAddressBookContactMutation();

  // Wait until the organisation and its policies have resolved before drawing
  // any conclusion about the address-book configuration.
  if (!organisation || policiesUninitialized || policiesLoading) {
    return <BarLoader color="gray" />;
  }

  if (policiesError) {
    return (
      <InfoBar
        text="Couldn't load the organisation policies. Please try again."
        variant="error"
      />
    );
  }

  // Policies are known: a missing id genuinely means none is configured.
  if (!addressBookId) {
    return (
      <InfoBar
        text="No default address book is configured for this organisation."
        variant="info"
      />
    );
  }

  if (isFetching) {
    return <BarLoader color="gray" />;
  }

  if (isError) {
    return (
      <InfoBar
        text="Couldn't load the default address book — the Alexa address book service returned an error."
        variant="error"
      >
        <AddressBookErrorDetails
          addressBookId={addressBookId}
          error={error}
          organisationId={organisation.id}
        />
      </InfoBar>
    );
  }

  if (!addressBook) {
    // The query has not yet run to completion.
    return <BarLoader color="gray" />;
  }

  const details = [];
  let primaryContact;
  for (const contact of addressBook.contacts) {
    if (contact.phoneNumber == '22222222') {
      primaryContact = contact;
    } else {
      details.push(
        <Label>
          {contact.name}: {contact.phoneNumber}
        </Label>,
      );
      details.push(
        <Button
          className="place-self-end"
          onClick={() => {
            deleteAddressBookContact({
              organisationId: organisation.id,
              addressBookId: addressBook.addressBookId,
              contactId: contact.contactId ?? '',
              actionType: 'deleteAddressBookContact',
            });
          }}
          variant="primary-warning"
        >
          Delete
          <TrashIcon className="h-6" />
        </Button>,
      );
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <SectionHeadingGroup
          className="max-w-96"
          subtitle="Contacts all your users can call"
          title="Default Contacts:"
        />
        <div className="grid-row grid grid-cols-2 items-center gap-2">
          {details}
        </div>
      </div>
      <div>
        <SectionHeadingGroup
          className="max-w-96"
          subtitle="Add a new contact for all your users"
          title="Add contact:"
        />
        <CreateContactForm addressBookId={addressBookId} />
      </div>
      <div className="gap-2">
        <SectionHeadingGroup
          className="max-w-96"
          subtitle="Adjust the name users see when they get a call from the DMP"
          title="Change DMP/ARC caller name:"
        />
        <p className="pt-4 italic">DMP/ARC caller name:</p>
        <p className="font-extrabold">{primaryContact?.name}</p>
        <CreateContactForm
          addressBookId={addressBookId}
          contactFormType={'primaryContact'}
          contactId={primaryContact?.contactId}
        />
      </div>
    </div>
  );
};
